/**
 * Runtime half of the guarded production-checkout contract.
 *
 * `rollback` is a deliberately narrow, separately-authorised read-only mode:
 * new checkout/order/payment writes are blocked, while existing anonymous
 * Order Details links remain readable. The build guard pins the accompanying
 * capability matrix and executes this module from the emitted artefact.
 */

export type ProductionCheckoutMode = "enabled" | "rollback" | "unconfigured";

export function parseProductionCheckoutMode(raw: unknown): ProductionCheckoutMode {
  const value = String(raw ?? "").toLowerCase();
  if (value === "enabled" || value === "rollback") return value;
  return "unconfigured";
}

export const PRODUCTION_CHECKOUT_MODE = parseProductionCheckoutMode(
  import.meta.env.VITE_PRODUCTION_CHECKOUT_MODE,
);

export const CHECKOUT_READ_ONLY_ROLLBACK_ENABLED = PRODUCTION_CHECKOUT_MODE === "rollback";

export class CheckoutMutationBlockedError extends Error {
  readonly code = "CHECKOUT_READ_ONLY_ROLLBACK";

  constructor(operation: string) {
    super(`Checkout mutation blocked during read-only rollback: ${operation}`);
    this.name = "CheckoutMutationBlockedError";
  }
}

export function assertCheckoutMutationAllowed(
  operation: string,
  mode: ProductionCheckoutMode = PRODUCTION_CHECKOUT_MODE,
): void {
  if (mode === "rollback") throw new CheckoutMutationBlockedError(operation);
}

export function isCheckoutMutationBlockedError(error: unknown): boolean {
  return (
    error instanceof CheckoutMutationBlockedError ||
    (error instanceof Error &&
      "code" in error &&
      (error as Error & { code?: unknown }).code === "CHECKOUT_READ_ONLY_ROLLBACK")
  );
}
